// src/features/MediaLibrary/AudioService.js - Fixed upload issue
import { 
  collection, 
  doc, 
  getDoc, 
  getDocs, 
  setDoc, 
  updateDoc, 
  deleteDoc, 
  query, 
  where, 
  orderBy, 
  serverTimestamp 
} from 'firebase/firestore';
import { 
  ref, 
  uploadBytes, 
  getDownloadURL, 
  deleteObject 
} from 'firebase/storage';
import { db, storage } from '../../firebase/config';

/**
 * Service for managing audio assets in Firestore and Firebase Storage
 */
const AudioService = {
  /**
   * Upload an audio file to Firebase Storage and save metadata to Firestore
   * @param {File} file - The audio file to upload
   * @param {Object} metadata - Additional metadata for the audio file
   * @returns {Promise<Object>} The saved audio asset document
   */
  async uploadAudio(file, metadata = {}) {
    try {
      // Create a reference to the audio file in Firebase Storage
      const fileName = `${Date.now()}_${file.name.replace(/\s+/g, '_')}`;
      const fileRef = ref(storage, `audio/${fileName}`);
      
     // Upload the file to Firebase Storage
      const uploadResult = await uploadBytes(fileRef, file);
           
      // Get the download URL for the file
      const downloadURL = await getDownloadURL(uploadResult.ref);
   
      // Create a document in the audioAssets collection
      const audioCollectionRef = collection(db, 'audioAssets');
      const docRef = doc(audioCollectionRef);
      
      const audioAsset = {
        id: docRef.id,
        name: file.name,
        url: downloadURL,
        storagePath: uploadResult.ref.fullPath, // Use the path from the result
        mimeType: file.type,
        size: file.size,
        createdAt: serverTimestamp(),
        ...metadata
      };
      
      // Save the audio asset document to Firestore
      await setDoc(docRef, audioAsset);
      return {
        ...audioAsset,
        createdAt: new Date()
      };
    } catch (error) {
      console.error('Error uploading audio file:', error);
      throw error;
    }
  },
  
  /**
   * Get all audio assets from Firestore
   * @returns {Promise<Array>} Array of audio asset documents
   */
  async getAudioAssets() {
    try {
      
      const audioCollectionRef = collection(db, 'audioAssets');
      const q = query(
        audioCollectionRef,
        orderBy('createdAt', 'desc')
      );
      
      const snapshot = await getDocs(q);
      
      const assets = snapshot.docs.map(docSnapshot => ({
        ...docSnapshot.data(),
        createdAt: docSnapshot.data().createdAt?.toDate()
      }));
      return assets;
    } catch (error) {
      console.error('Error fetching audio assets:', error);
      throw error;
    }
  },
  
  /**
   * Get a single audio asset by ID
   * @param {string} id - The ID of the audio asset to get
   * @returns {Promise<Object>} The audio asset document
   */
  async getAudioAsset(id) {
    try {

      const docRef = doc(db, 'audioAssets', id);
      const docSnapshot = await getDoc(docRef);
      
      if (!docSnapshot.exists()) {
        console.error(`Audio asset with ID ${id} not found`);
        throw new Error(`Audio asset with ID ${id} not found`);
      }
      
      const asset = {
        ...docSnapshot.data(),
        createdAt: docSnapshot.data().createdAt?.toDate()
      };
      return asset;
    } catch (error) {
      console.error('Error fetching audio asset:', error);
      throw error;
    }
  },
  
  /**
   * Delete an audio asset from Firestore and Firebase Storage
   * @param {Object} audioAsset - The audio asset to delete
   * @returns {Promise<void>}
   */
  async deleteAudioAsset(audioAsset) {
    try {
  
      // Delete the file from Firebase Storage
      if (audioAsset.storagePath) {
        const fileRef = ref(storage, audioAsset.storagePath);
        await deleteObject(fileRef);

      }
      
      // Delete the document from Firestore
      const docRef = doc(db, 'audioAssets', audioAsset.id);
      await deleteDoc(docRef);
    } catch (error) {
      console.error('Error deleting audio asset:', error);
      throw error;
    }
  },
  
  /**
   * Update an audio asset's metadata in Firestore
   * @param {string} id - The ID of the audio asset to update
   * @param {Object} updates - The updates to apply to the audio asset
   * @returns {Promise<void>}
   */
  async updateAudioAsset(id, updates) {
    try {
      const docRef = doc(db, 'audioAssets', id);
      
      await updateDoc(docRef, {
        ...updates,
        updatedAt: serverTimestamp()
      });
    } catch (error) {
      console.error('Error updating audio asset:', error);
      throw error;
    }
  },
  
  /**
   * Get audio assets for a specific unit
   * @param {string} unitId - The ID of the unit
   * @returns {Promise<Array>} Array of audio assets for the unit
   */
  async getUnitAudioAssets(unitId) {
    try {

      const audioCollectionRef = collection(db, 'audioAssets');
      const q = query(
        audioCollectionRef,
        where('unitId', '==', unitId),
        orderBy('createdAt', 'desc')
      );
      
      const snapshot = await getDocs(q);
      
      const assets = snapshot.docs.map(docSnapshot => ({
        ...docSnapshot.data(),
        createdAt: docSnapshot.data().createdAt?.toDate()
      }));
                  return assets;
    } catch (error) {
      console.error('Error fetching unit audio assets:', error);
      throw error;
    }
  }
};

// Add a test method to verify the service is working
AudioService.test = () => {
  console.log('AudioService is loaded and working!');
  return true;
};

export default AudioService;